import React, { useState, useEffect } from "react";
import logo from "../../Assets/Kalam Kids Logo_newlogo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import { useParams, useLocation } from 'react-router-dom';
import { getExpansiveById } from '../../api/Api'; // Adjust the import path as necessary

const ViewExpansive = () => {
  const { id } = useParams();
  const [expansive, setExpansive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExpansive = async () => {
      try {
        const response = await getExpansiveById(id);
        if (response.success) {
          setExpansive(response.data);
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError("Failed to fetch expansive details.");
        console.error("Error fetching expansive:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchExpansive();
    }
  }, [id]);

  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('print') === 'true' && !loading && expansive) {
      // Small delay to ensure rendering is complete
      const timer = setTimeout(() => {
        handlePrint();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.search, loading, expansive]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <div className="text-center mt-10">Loading...</div>;
  }

  if (error) {
    return <div className="text-center mt-10 text-red-500">Error: {error}</div>;
  }

  if (!expansive) {
    return <div className="text-center mt-10">No expansive data found.</div>;
  }
  
  return (
    <div className="max-w-3xl mx-auto bg-white p-8 font-[Poppins] text-sm text-gray-800" id="voucher-printable-content">
      {/* Print Styles */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #voucher-printable-content, #voucher-printable-content * {
              visibility: visible;
            }
            #voucher-printable-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              height: 100%;
              margin: 0;
              padding: 20px;
              background-color: white;
              font-family: 'Poppins', sans-serif;
            }
            .print\\:hidden {
              display: none !important;
            }
            /* Ensure table borders print correctly */
            table, th, td {
               border-bottom: 1px solid #e5e7eb; 
            }
            th {
               border-bottom: 2px solid #e5e7eb;
            }
          }
        `}
      </style>

      {/* Header */}
      <div className="flex justify-between items-start mb-8 border-b-2 border-gray-100 pb-6">
        <img src={logo} alt="Kalam Kids Logo" className="h-16 w-auto object-contain" />
        <div className="text-right">
            <h1 className="text-2xl font-bold text-gray-800">Voucher Receipt</h1>
            <p className="text-gray-500 text-xs mt-1">Generated on {new Date().toLocaleDateString()}</p>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-12 mb-8">
        {/* Payee Details */}
        <div>
            <h3 className="text-[#16bab1] font-bold uppercase text-xs mb-3 tracking-wider">Payee Details</h3>
            <div className="space-y-2">
                <div className="flex flex-col">
                    <span className="text-xs text-gray-500 font-bold">Payee Name</span>
                    <span className="font-semibold text-gray-900">{expansive.payeeName}</span>
                </div>
                <div className="flex flex-col">
                    <span className="text-xs text-gray-500 font-bold">Phone</span>
                    <span className="font-medium text-gray-900">{expansive.payeePhoneNumber}</span>
                </div>
                <div className="flex flex-col">
                    <span className="text-xs text-gray-500 font-bold">Category</span>
                    <span className="font-medium text-gray-900">{expansive.category || "N/A"}</span>
                </div>
                <div className="flex flex-col">
                    <span className="text-xs text-gray-500 font-bold">Particulars</span>
                    <span className="font-medium text-gray-900">{expansive.particulars}</span>
                </div>
            </div>
        </div>

        {/* Voucher Details */}
        <div>
            <h3 className="text-[#16bab1] font-bold uppercase text-xs mb-3 tracking-wider">Voucher Details</h3>
            <div className="space-y-2">
                <div className="flex flex-col">
                    <span className="text-xs text-gray-500 font-bold">Invoice No</span>
                    <span className="font-semibold text-gray-900">{expansive.invoiceNo}</span>
                </div>
                <div className="flex flex-col">
                    <span className="text-xs text-gray-500 font-bold">Date</span>
                    <span className="font-medium text-gray-900">
                         {expansive.approvalDate ? new Date(expansive.approvalDate).toLocaleDateString() : "N/A"}
                    </span>
                </div>
                <div className="flex flex-col">
                    <span className="text-xs text-gray-500 font-bold">Payment Mode</span>
                    <span className="font-medium text-gray-900">{expansive.paymentMode || "N/A"}</span>
                </div>
                 <div className="flex flex-col">
                    <span className="text-xs text-gray-500 font-bold">Approved</span>
                    <span className="font-medium text-gray-900">{expansive.approved ? "Yes" : "No"}</span>
                </div>
            </div>
        </div>
      </div>

      {/* Items / Description Table */}
      <div className="mb-8">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-gray-100">
              <th className="py-3 text-left text-sm font-bold text-gray-800">Description</th>
              <th className="py-3 text-right text-sm font-bold text-gray-800">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
             {/* Assuming single item or mapping if logic changes, currently hardcoded as per original */}
             <tr>
                <td className="py-4 text-gray-600">Voucher Payment</td>
                <td className="py-4 text-right font-medium text-gray-900">₹{expansive.amount}</td>
             </tr>
            {/* Total Row */}
            <tr className="border-t border-gray-200">
                <td className="py-4 text-right pr-4 font-bold text-gray-800">Total Amount</td>
                <td className="py-4 text-right font-bold text-gray-900">₹{expansive.amount}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Amount In Words */}
      <div className="mb-12 border-t border-gray-100 pt-4">
        <p className="text-xs text-gray-500 font-bold mb-1">Amount in words:</p>
        <p className="font-medium text-gray-800 capitalize italic">
            {expansive.amountInWords} Rupees Only
        </p>
      </div>

      {/* Footer / Accountant */}
      <div className="flex justify-between items-end mt-20 pt-8 border-t border-gray-100 relative">
         <div>
            <p className="text-xs text-gray-500 font-bold mb-1">Accountant</p>
            <p className="font-semibold text-gray-800">{expansive.AccountentName || "N/A"}</p>
         </div>
         <div className="text-right text-xs text-gray-500">
            <p className="font-bold text-[#16bab1] mb-1">www.kalamkids.in</p>
            <p>#23, New Shankaranpalayam Road, Near Tollgate,</p>
            <p>Vellore - 632001, TN</p>
         </div>
         <div className="absolute -bottom-8 left-0 right-0 text-center print:block hidden">
             <p className="text-[10px] text-[#16bab1]">Designed and Developed by Green Emerald Branding Agency</p>
         </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex justify-center gap-3 print:hidden border-t pt-6">
        <button
          onClick={handlePrint}
          className="px-6 py-2 rounded-lg bg-[#16bab1] text-white hover:bg-[#139a93] font-medium transition shadow-sm flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faPrint} /> Print Voucher
        </button>
      </div>

    </div>
  );
};

export default ViewExpansive;
